import { Button } from "@/components/ui/button"
import ErrorScreen from "@/components/ui/error-screen"
import { FieldGroup } from "@/components/ui/field-group"
import { FormActions } from "@/components/ui/form-actions"
import { RhfField } from "@/components/ui/rhf-field"
import { useToast } from "@/components/ui/use-toast"
import { gql } from "@/generated"
import { displayValidationErrors } from "@/util/validations"
import { useQuery } from "@apollo/client"
import { zodResolver } from "@hookform/resolvers/zod"
import React from "react"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { z, ZodType } from "zod"

const States_Query = gql(`
  query States {
    geographicStates {
      id
      name
    }
  }
`)

const Programs_Query = gql(`
  query Programs($geographicStateId: ID) {
    programs(
      geographicStateId: $geographicStateId
    ) {
      nodes {
        id
        name
        disclosureBookletUrl
        sponsorState {
          name
        }
      }
    }
  }
`)

export type FiveTwoNineAccountFormValues = {
  sponsorStateId: string
  programId: string
  accountNumber: string
  beneficiaryFirstName: string
  beneficiaryLastName: string
  beneficiaryBirthdate?: string
}

const schema: ZodType<FiveTwoNineAccountFormValues> = z.object({
  sponsorStateId: z.string().min(1, "State sponsor is required"),
  programId: z.string().min(1, "Program is required"),
  accountNumber: z.string().min(1, "Account number is required"),
  beneficiaryFirstName: z.string().min(1, "First name is required"),
  beneficiaryLastName: z.string().min(1, "Last name is required"),
  beneficiaryBirthdate: z.string().optional(),
})

const FiveTwoNineAccountForm = ({
  onSubmit,
  defaultValues,
  isNew,
}: {
  onSubmit: SubmitHandler<FiveTwoNineAccountFormValues>
  defaultValues: FiveTwoNineAccountFormValues
  isNew?: boolean
}) => {
  const methods = useForm<FiveTwoNineAccountFormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  })
  const { control, formState, setError } = methods
  const geographicStateId = methods.watch("sponsorStateId")

  const {
    data: statesData,
    loading: statesLoading,
    error: statesError,
  } = useQuery(States_Query)
  const {
    data: programsData,
    loading: programsLoading,
    error: programsError,
  } = useQuery(Programs_Query, {
    variables: {
      geographicStateId,
    },
    skip: !geographicStateId,
  })

  const loading = statesLoading || programsLoading
  const error = statesError || programsError

  const { toast } = useToast()

  const onInternalSubmit = async (data: FiveTwoNineAccountFormValues) => {
    try {
      await onSubmit(data)
    } catch (e: any) {
      console.error(e)
      displayValidationErrors({
        recordType: "user",
        errors: e,
        setError,
        toast,
        validationErrorTitle: "Beneficiary was not updated.",
      })
    }
  }

  if (loading && !statesData) return null
  if (error) return <ErrorScreen message={error?.message} />

  const states = statesData?.geographicStates?.map((s) => ({
    label: s.name,
    value: s.id,
  }))

  const programsList = programsData?.programs?.nodes
    ? programsData.programs.nodes.map((s: { name: string; id: string }) => ({
        label: s.name,
        value: s.id,
      }))
    : []

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onInternalSubmit)}>
        <FieldGroup row={true}>
          <RhfField
            name="beneficiaryFirstName"
            control={control}
            placeholder="First name"
            label="First Name"
            autoFocus={true}
          />
          <RhfField
            name="beneficiaryLastName"
            control={control}
            placeholder="Last name"
            label="Last Name"
          />
        </FieldGroup>
        <FieldGroup>
          <RhfField
            name="beneficiaryBirthdate"
            control={control}
            type="date"
            label="Birthdate (Optional)"
          />
        </FieldGroup>
        <FieldGroup>
          <RhfField
            name="accountNumber"
            control={control}
            placeholder="0000 0000 0000 0000"
            label="Account Number"
          />
        </FieldGroup>
        <FieldGroup>
          <RhfField
            name="sponsorStateId"
            control={control}
            placeholder="Select state"
            label="State Sponsor"
            as="select"
            options={states}
            afterChange={() => {
              methods.setValue("programId", "")
            }}
          />
        </FieldGroup>
        <FieldGroup>
          <RhfField
            name="programId"
            control={control}
            placeholder="Select program"
            label="Program Name"
            as="select"
            disabled={programsList.length === 0}
            options={programsList}
          />
        </FieldGroup>
        <FormActions>
          <Button
            type="submit"
            variant="default"
            disabled={formState.isSubmitting}
          >
            {isNew ? "Create" : "Update"}
          </Button>
        </FormActions>
      </form>
    </FormProvider>
  )
}

export default FiveTwoNineAccountForm
